import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  SignupSourceCard,
  SIGNUP_SOURCE_ASKED_STORAGE_KEY,
} from '@/features/onboarding/SignupSourceCard';
import { ASSETS } from '@/lib/config';
import { useAuthStore } from '@/stores/authStore';
import { STRINGS } from '@/strings/strings';

/**
 * Marketing attribution package (2026-07-17), Part C mount site.
 *
 * The SignupSourceCard renders ABOVE the hero when:
 *   1. user.signupSource === null (backend-persisted, cross-session gate)
 *   2. sessionStorage[SIGNUP_SOURCE_ASKED_STORAGE_KEY] is not set
 *      (same-session gate — the card sets this on submit AND on skip so
 *      it never re-asks after either action within the same tab).
 *
 * Both conditions must hold, so once a user answers, the backend gate
 * hides the card everywhere; if they only skip, they see it again in
 * the next session (deliberate — a skip isn't an answer).
 *
 * The "Los geht's" CTA is NEVER blocked by the card state.
 */
function shouldShowSignupSourceCard(
  user: {
    signupSource: string | null;
  } | null,
): boolean {
  if (!user) return false;
  if (user.signupSource !== null) return false;
  try {
    if (window.sessionStorage.getItem(SIGNUP_SOURCE_ASKED_STORAGE_KEY)) {
      return false;
    }
  } catch {
    // Safari Private Mode / SSR — fall through and render. A card that
    // renders when it shouldn't is a smaller failure mode than the
    // silent-drop-on-storage-throw alternative.
  }
  return true;
}

export function WelcomeRoute() {
  const user = useAuthStore((s) => s.user);
  const showSourceCard = shouldShowSignupSourceCard(user);

  return (
    <div className="text-center">
      {showSourceCard && <SignupSourceCard />}

      <img
        src={ASSETS.kaptnKuschelImage}
        alt={STRINGS.onboarding.welcome.kaptnKuschelAlt}
        loading="eager"
        className="mx-auto mb-6 w-40 md:w-48 h-auto"
      />
      <h1 className="text-[22px] font-semibold text-dream-white mb-3">
        {STRINGS.onboarding.welcome.title}
      </h1>
      <p className="text-sm text-dream-white/90 leading-relaxed mb-8">
        {STRINGS.onboarding.welcome.body}
      </p>
      <Button asChild className="w-full h-12 text-base font-semibold">
        <Link to="/onboarding/child_profile">{STRINGS.onboarding.welcome.cta}</Link>
      </Button>
    </div>
  );
}
